// PortalGuideModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/portalguide.css";
import {
  faXmark,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const StepIllustration = ({ step }) => {
  const images = {
    1: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=80",
    2: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80",
    3: "https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&w=1200&q=80",
    4: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80",
  };

  const captions = {
    1: "Password",
    2: "Profile",
    3: "Courses",
    4: "Fees",
  };

  return (
    <div
      className="guide-portal-image-card"
      style={{ backgroundImage: `url(${images[step] || images[1]})` }}
      aria-hidden="true"
    >
      <div className="guide-portal-image-overlay" />
      <div className="guide-portal-image-badge">
        <FontAwesomeIcon icon={faCircleCheck} />
        <span>{captions[step] || "Guide"}</span>
      </div>
    </div>
  );
};

const PortalGuideModal = ({ onClose, onHideForever }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  const slides = useMemo(
    () => [
      {
        step: 1,
        title: "Change your password first",
        text:
          "Once you log in, go to Change Password and update your password immediately. After changing it, the default password will no longer work when you log in again.",
        action: "Change Password",
        path: "/change-password",
      },
      {
        step: 2,
        title: "Edit your profile",
        text:
          "If this is your first time logging in, open Edit Profile and complete your biodata carefully.",
        action: "Edit Profile",
        path: "/student-profile",
      },
      {
        step: 3,
        title: "Register your courses",
        text:
          "Go to Add Course to register. If your payment has not been made yet, course registration will not be allowed.",
        action: "Add Course",
        path: "/add-course",
      },
      {
        step: 4,
        title: "Pay fees and print slip",
        text:
          "Proceed to Pay Fees to generate your Remita number. After payment, you can print your payment slip directly from the site.",
        action: "Pay Fees",
        path: "/pay-fees",
      },
    ],
    []
  );

  const goToSlide = (index) => {
    const total = slides.length;
    if (!total) return;
    setCurrentSlide((index + total) % total);
  };

  const goNext = () => goToSlide(currentSlide + 1);
  const goPrev = () => goToSlide(currentSlide - 1);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const handleTouchStart = (e) => {
    setTouchEndX(null);
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;

    const distance = touchStartX - touchEndX;
    const minSwipeDistance = 40;

    if (distance > minSwipeDistance) goNext();
    if (distance < -minSwipeDistance) goPrev();

    setTouchStartX(null);
    setTouchEndX(null);
  };

  return (
    <div className="guide-portal-overlay" role="dialog" aria-modal="true">
      <div className="guide-portal-modal">
        <div className="guide-portal-modal-header">
          <div className="guide-portal-header-text">
            <p className="guide-portal-eyebrow">Quick portal guide</p>
            <h2>How to use your student portal</h2>
          </div>

          <button
            className="guide-portal-close"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="guide-portal-modal-body">
          <button
            type="button"
            className="guide-portal-nav guide-portal-nav-left"
            onClick={goPrev}
            aria-label="Previous slide"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <div
            className="guide-portal-carousel-viewport"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="guide-portal-carousel-track"
              style={{
                transform: `translateX(-${currentSlide * 100}%)`,
                width: `${slides.length * 100}%`,
              }}
            >
              {slides.map((item) => (
                <div key={item.step} className="guide-portal-slide">
                  <div className="guide-portal-slide-art">
                    <StepIllustration step={item.step} />
                  </div>

                  <div className="guide-portal-step-content">
                    <span className="guide-portal-step-number">
                      Step {item.step}
                    </span>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>

                    <button
                      type="button"
                      className="guide-portal-step-button"
                      onClick={() => navigate(item.path)}
                    >
                      {item.action}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="guide-portal-nav guide-portal-nav-right"
            onClick={goNext}
            aria-label="Next slide"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        <div className="guide-portal-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`guide-portal-dot ${index === currentSlide ? "active" : ""}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <div className="guide-portal-footer">
          <label className="guide-portal-check">
            <input
              type="checkbox"
              onChange={(e) => (e.target.checked ? onHideForever() : null)}
            />
            Do not show this again
          </label>

          <button
            type="button"
            className="guide-portal-later"
            onClick={onClose}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortalGuideModal;
